import { Link, useParams } from "react-router";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
	ScheduleGantt,
	ScheduleGanttSkeleton,
} from "@/features/schedules/components/schedule-gantt";
import { useSchedules } from "@/features/schedules/hooks/use-schedules.ts";
import {
	formatScheduleRange,
	formatScheduleTotal,
} from "@/features/schedules/utils/gantt.ts";
import { useActiveObra } from "@/features/stages/hooks/use-active-obra.ts";
import { usePermissions } from "@/hooks/use-permissions.ts";

export function CronogramaPage() {
	const { obra: obraKey } = useParams();
	const { has, isLoading: permsLoading } = usePermissions();
	const {
		obra,
		isLoading: obraLoading,
		isError: obraError,
		refetch: refetchObra,
	} = useActiveObra();

	const canView = has("construction_stage_schedules:view");
	const canManage = has("construction_stage_schedules:manage");
	const canMeasure = has("stage_measurements:create");
	const canViewNotes = has("schedule_notes:view");
	const canManageNotes = has("schedule_notes:manage");
	const schedulesQuery = useSchedules(canView ? obra?.id : undefined);

	const isLoading =
		permsLoading ||
		obraLoading ||
		(!!obra && canView && schedulesQuery.isPending);

	const span = schedulesQuery.data?.span;
	const range = span ? formatScheduleRange(span.start, span.end) : null;
	const total = span ? formatScheduleTotal(span.start, span.end) : null;

	function body() {
		if (obraError) {
			return (
				<Card>
					<CardContent className="card-state">
						<p>Não foi possível carregar a obra.</p>
						<Button variant="outline" onClick={() => refetchObra()}>
							Tentar novamente
						</Button>
					</CardContent>
				</Card>
			);
		}
		if (isLoading || !obra) {
			return <ScheduleGanttSkeleton />;
		}
		if (!canView) {
			return (
				<Card>
					<CardContent className="card-state">
						<p>Você não tem permissão para ver o cronograma desta obra.</p>
					</CardContent>
				</Card>
			);
		}
		if (schedulesQuery.isError) {
			return (
				<Card>
					<CardContent className="card-state">
						<p>Não foi possível carregar o cronograma.</p>
						<Button variant="outline" onClick={() => schedulesQuery.refetch()}>
							Tentar novamente
						</Button>
					</CardContent>
				</Card>
			);
		}
		const data = schedulesQuery.data;
		if (!data) return <ScheduleGanttSkeleton />;
		return (
			<ScheduleGantt
				constructionId={obra.id}
				construction={data.construction}
				nodes={data.nodes}
				span={data.span}
				canManage={canManage}
				canMeasure={canMeasure}
				canViewNotes={canViewNotes}
				canManageNotes={canManageNotes}
			/>
		);
	}

	return (
		<div className="cronograma-page">
			<div className="page-header">
				<div>
					<h1 className="page-title">Cronograma da Obra</h1>
					<p className="page-subtitle">
						{range && total
							? `Planejado: ${range} · ${total}`
							: "Planeje as datas de início e término das etapas."}
					</p>
				</div>
				<div className="page-header__actions">
					{obra && (
						<Button asChild variant="outline">
							<Link to={`/${obraKey}/etapas`}>Ver etapas</Link>
						</Button>
					)}
				</div>
			</div>

			{body()}
		</div>
	);
}
